import { Link } from 'react-router-dom';

import { useCartContents } from '../hooks/queries';
import { useOverlay } from '../hooks/useOverlay';
import { formatPrice } from '../lib/money';
import { useCartStore } from '../stores/cart-store';
import { useUiStore } from '../stores/ui-store';
import { EmptyState, ErrorState, Skeleton } from './States';

/**
 * The bag.
 *
 * Quantities come from the persisted store; every price and title is read live
 * from the product queries and joined at render. Nothing displayed here is a
 * copy of server data, so a price change on the backend is reflected the next
 * time the drawer opens rather than sitting stale in someone's bag.
 */
export function CartDrawer() {
  const open = useUiStore((state) => state.cartOpen);
  const closeCart = useUiStore((state) => state.closeCart);
  const containerRef = useOverlay(open, closeCart);

  const { lines, subtotalPaise, isLoading, isError, hasUnavailable } = useCartContents();
  const setQuantity = useCartStore((state) => state.setQuantity);
  const remove = useCartStore((state) => state.remove);

  if (!open) return null;

  return (
    <>
      <div className="drawer-backdrop" onClick={closeCart} aria-hidden="true" />
      <div
        ref={containerRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Shopping bag"
        tabIndex={-1}
      >
        <div className="drawer__head">
          <h2 className="label label--bright">Your bag</h2>
          <button type="button" className="icon-button" onClick={closeCart}>
            Close
          </button>
        </div>

        <div className="drawer__body">
          {isLoading && lines.length > 0 ? (
            <div className="stack">
              <Skeleton className="skeleton--text" />
              <Skeleton className="skeleton--text" />
            </div>
          ) : null}

          {isError ? <ErrorState error={new Error('Could not load your bag')} /> : null}

          {!isLoading && lines.length === 0 ? (
            <EmptyState
              title="Your bag is empty"
              body="Nothing chosen yet."
              action={
                <Link to="/shop" className="btn btn--sm" onClick={closeCart}>
                  Browse the shop
                </Link>
              }
            />
          ) : null}

          <ul>
            {lines.map(({ line, product, variant, unavailable, lineTotalPaise }) => (
              <li key={line.variantId} className="line-item">
                <div className="line-item__media">
                  {product?.images[0] ? (
                    <img src={product.images[0].url} alt="" loading="lazy" decoding="async" />
                  ) : null}
                </div>

                <div>
                  <p style={{ marginBottom: '0.25rem' }}>
                    {product ? (
                      <Link to={`/product/${product.slug}`} onClick={closeCart}>
                        {product.title}
                      </Link>
                    ) : (
                      <span className="dim">Loading…</span>
                    )}
                  </p>
                  <p className="label">{variant?.title ?? line.variantId}</p>

                  {unavailable ? (
                    // Stated plainly and blocking checkout, rather than
                    // discovered at the payment step.
                    <p className="label" style={{ color: 'var(--danger)' }}>
                      No longer available
                    </p>
                  ) : (
                    <div className="qty mt-sm">
                      <button
                        type="button"
                        onClick={() => setQuantity(line.variantId, line.quantity - 1)}
                        aria-label={`Decrease quantity of ${product?.title ?? 'item'}`}
                      >
                        −
                      </button>
                      <output aria-live="polite">{line.quantity}</output>
                      <button
                        type="button"
                        onClick={() => setQuantity(line.variantId, line.quantity + 1)}
                        disabled={line.quantity >= 10}
                        aria-label={`Increase quantity of ${product?.title ?? 'item'}`}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ textAlign: 'right' }}>
                  <p className="mono" style={{ fontSize: 'var(--text-small)' }}>
                    {formatPrice(lineTotalPaise)}
                  </p>
                  <button
                    type="button"
                    className="label mt-sm"
                    onClick={() => remove(line.variantId)}
                    style={{ textDecoration: 'underline' }}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {lines.length > 0 ? (
          <div className="drawer__foot stack">
            <div className="totals">
              <span>Subtotal</span>
              <span>{formatPrice(subtotalPaise)}</span>
            </div>
            <p className="label faint">
              Taxes and shipping calculated at checkout
            </p>
            <Link
              to="/checkout"
              className="btn btn--primary btn--block"
              onClick={closeCart}
              aria-disabled={hasUnavailable}
              // A bag containing a withdrawn variant cannot become an order;
              // stopping it here is kinder than failing at payment.
              style={hasUnavailable ? { pointerEvents: 'none', opacity: 0.4 } : undefined}
            >
              Checkout
            </Link>
          </div>
        ) : null}
      </div>
    </>
  );
}
